import '@kerfjs/ui/select/register';
import '@kerfjs/ui/text-field.css';

import { Button } from '@kerfjs/ui/button';
import { px } from '@kerfjs/ui/css-values';
import { Disclosure } from '@kerfjs/ui/disclosure';
import { FramedList, FramedListRow } from '@kerfjs/ui/framed-list';
import { Grid } from '@kerfjs/ui/grid';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { Select } from '@kerfjs/ui/select';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import type { SafeHtml } from 'kerfjs';

import type { QuoteMatrix } from '../../../data/api.js';
import { PRICE_BASES, quoteDialog, quoteTaxBasis } from '../../../data/state.js';
import { QuoteViewDialog } from './quote-view-dialog.js';

export function QuoteDialog({ matrix: m }: { matrix: QuoteMatrix }): SafeHtml {
  const dlg = quoteDialog.value;
  if (dlg === null) return <></>;
  if (dlg.mode === 'view') return <QuoteViewDialog matrix={m} quotationId={dlg.quotationId} />;
  const editing = dlg.mode === 'edit';
  const col = editing ? m.columns.find((c) => c.quotationId === dlg.quotationId) : undefined;
  const title = editing ? `Edit Quotation${col !== undefined ? ` — ${col.vendorName}` : ''}` : 'Add a Quotation';
  const vendor = col?.vendorName ?? '';
  const currency = col?.currency ?? m.base;
  const basis = col?.priceBasis ?? 'delivered-to-site';
  return (
    <DialogSurface
      label={title}
      className="quote-dialog"
      rootAttributes={{ 'data-unsaved-guard': 'this quote', 'data-auto-open-dialog': '' }}
      footer={
        <>
          <Button
            tone="neutral"
            size="default"
            emphasis="plain"
            action="quote-dialog-cancel"
            rootAttributes={{ 'data-unsaved-cancel': '', 'data-dialog-close': '' }}
          >
            Cancel
          </Button>
          <Button
            size="default"
            tone="brand"
            emphasis="primary"
            action="quote-dialog-save"
            rootAttributes={{ 'data-unsaved-save': '' }}
          >
            {editing ? 'Save changes' : 'Save quotation'}
          </Button>
        </>
      }
    >
      <List gap="m">
        <TextField type="text" name="q-vendor" label="Vendor" placeholder="Vendor name" value={vendor} autofocus />
        <TextField type="text" name="q-currency" label="Currency" value={currency} />
        <Select<string>
          name="q-basis"
          label="Price Basis"
          value={basis}
          choices={PRICE_BASES.map((b) => ({ value: b, label: b }))}
        />
        {editing && col?.sourceFilename !== null && col?.sourceFilename !== undefined ? (
          <Text tone="quiet" size="compact" wrap="anywhere">
            Source File: {col.sourceFilename}
          </Text>
        ) : null}
      </List>
      {!editing ? (
        [
          <ListHeader label="Prices" />,
          m.lineItems.length === 0 ? (
            <Text tone="quiet" size="compact">
              Add line items first to price them.
            </Text>
          ) : (
            <FramedList label="Demand line prices">
              {m.lineItems.map((li) => (
                <FramedListRow rootAttributes={{ 'data-key': li.id }}>
                  <List gap="xs">
                    <Text flush>
                      {String(li.lineNumber)}. {li.description}
                    </Text>
                    <Grid minColumnWidth={px(150)} gap="xs">
                      <TextField
                        name={`q-line-qty-${li.id}`}
                        label="Quantity Offered"
                        size="compact"
                        type="number"
                        placeholder="—"
                      />
                      <TextField
                        size="compact"
                        type="number"
                        placeholder="—"
                        label={`Unit Price for Line ${String(li.lineNumber)}`}
                        rootAttributes={{ 'data-mline': li.id }}
                      />
                      <TextField
                        name={`q-line-total-${li.id}`}
                        label="Line Total"
                        size="compact"
                        type="number"
                        placeholder="—"
                      />
                    </Grid>
                  </List>
                </FramedListRow>
              ))}
            </FramedList>
          ),
          <Disclosure title="Other items — not on the demand" detail="Optional">
            <List gap="xs">
              <Text tone="quiet" size="compact">
                Add anything this vendor quoted that isn't a demand line; you can map it later.
              </Text>
              <FramedList label="Other quoted items">
                {[0, 1, 2].map((i) => (
                  <FramedListRow rootAttributes={{ 'data-key': `x${String(i)}` }}>
                    <List gap="xs">
                      <TextField
                        type="text"
                        size="compact"
                        placeholder="e.g. mounting bracket"
                        label="Extra Item Description"
                        rootAttributes={{ 'data-xline-desc': String(i) }}
                      />
                      <Grid minColumnWidth={px(150)} gap="xs">
                        <TextField
                          name={`q-extra-qty-${String(i)}`}
                          label="Quantity Offered"
                          size="compact"
                          type="number"
                          placeholder="—"
                        />
                        <TextField
                          label="Extra Item Unit Price"
                          size="compact"
                          type="number"
                          placeholder="—"
                          rootAttributes={{ 'data-xline-price': String(i) }}
                        />
                        <TextField
                          name={`q-extra-total-${String(i)}`}
                          label="Line Total"
                          size="compact"
                          type="number"
                          placeholder="—"
                        />
                      </Grid>
                    </List>
                  </FramedListRow>
                ))}
              </FramedList>
            </List>
          </Disclosure>,
          <ListHeader label="Quotation Total" />,
          <TextField name="q-grand-total" label="Grand Total as Quoted" type="number" placeholder="—" />,
          <Grid minColumnWidth={px(150)} gap="xs">
            <TextField name="q-tax-amount" label="Tax Amount" type="number" placeholder="—" />
            <TextField name="q-freight-amount" label="Freight Amount" type="number" placeholder="—" />
            <TextField name="q-discount-amount" label="Discount Amount" type="number" placeholder="—" />
          </Grid>,
          <Select<string>
            name="q-tax-basis"
            label="Do item prices include tax?"
            value={quoteTaxBasis.value}
            choices={[
              { value: 'unknown', label: 'Not Stated' },
              { value: 'included', label: 'Yes, Tax Included' },
              { value: 'excluded', label: 'No, Tax Excluded' },
            ]}
          />,
        ]
      ) : (
        <Text tone="quiet" size="compact">
          Correct individual cell prices in the matrix; here you can fix the vendor, currency, and price basis.
        </Text>
      )}
    </DialogSurface>
  );
}
